import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import QRCode from 'qrcode';
import { PDFDocument, rgb } from 'pdf-lib';
import { fetchStudents, clearStudents } from '../paperAssignmentSlice';

const QRCoverGenerator = ({ isOpen, onClose, levels, onGenerate }) => {
  const dispatch = useDispatch();
  const { students } = useSelector((state) => state.paperAssignment);

  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [bulkMode, setBulkMode] = useState(false);
  const [generating, setGenerating] = useState(false);

  // 브랜드 컬러
  const BRAND_COLOR = '#0E141B';
  const ACCENT_COLOR = '#C2452D';

  useEffect(() => {
    if (selectedLevel) {
      // 레벨 변경시 학생 목록 fetch 및 선택 초기화
      dispatch(fetchStudents(selectedLevel));
      setSelectedStudents([]);
    } else {
      dispatch(clearStudents());
    }
  }, [selectedLevel, dispatch]);

  if (!isOpen) return null;

  // Canvas로 한글 텍스트 이미지 생성
  const createTextImage = (text, fontSize = 24, fontWeight = 'normal', color = 'black') => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    ctx.font = `${fontWeight} ${fontSize}px "Pretendard", "Noto Sans KR", sans-serif`;
    const metrics = ctx.measureText(text);
    const textWidth = metrics.width;
    const textHeight = fontSize * 1.5;

    canvas.width = textWidth + 20;
    canvas.height = textHeight;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.font = `${fontWeight} ${fontSize}px "Pretendard", "Noto Sans KR", sans-serif`;
    ctx.fillStyle = color;
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 10, textHeight / 2);

    return canvas.toDataURL('image/png');
  };

  const formatGrade = (grade) => {
    const gradeNum = parseInt(grade);
    if (gradeNum >= 1 && gradeNum <= 6) return `초등 ${gradeNum}학년`;
    if (gradeNum >= 7 && gradeNum <= 9) return `중등 ${gradeNum - 6}학년`;
    if (gradeNum >= 10 && gradeNum <= 12) return `고등 ${gradeNum - 9}학년`;
    return `${grade}학년`;
  };

  const addPageToPDF = async (pdfDoc, level, student) => {
    const qrData = JSON.stringify({
      user_id: level.user_id,
      level_id: level.id,
      student_id: student.id,
      student_name: student.name,
      class_name: level.name
    });

    const qrCodeDataURL = await QRCode.toDataURL(qrData, {
      width: 300,
      margin: 2,
      color: { dark: '#000000', light: '#FFFFFF' }
    });

    const page = pdfDoc.addPage([595, 842]);
    const { width, height } = page.getSize();

    const qrImage = await pdfDoc.embedPng(qrCodeDataURL);
    const qrDims = qrImage.scale(0.8);

    // 타이틀 (배경 없이)
    const titleImageData = createTextImage('지필 과제 제출 표지', 32, 'bold', BRAND_COLOR);
    const titleImage = await pdfDoc.embedPng(titleImageData);
    const titleDims = titleImage.scale(0.8);
    page.drawImage(titleImage, {
      x: width / 2 - titleDims.width / 2,
      y: height - 80,
      width: titleDims.width,
      height: titleDims.height
    });

    // QR 코드 박스
    page.drawRectangle({
      x: width / 2 - qrDims.width / 2 - 20,
      y: height / 2 - qrDims.height / 2 + 60,
      width: qrDims.width + 40,
      height: qrDims.height + 40,
      borderColor: rgb(0.9, 0.9, 0.9),
      borderWidth: 3,
      color: rgb(1, 1, 1)
    });

    // QR 코드
    page.drawImage(qrImage, {
      x: width / 2 - qrDims.width / 2,
      y: height / 2 - qrDims.height / 2 + 80,
      width: qrDims.width,
      height: qrDims.height
    });

    // 학생 정보 (QR 코드 아래 한 줄로)
    const studentInfoText = `${student.name} | ${level.name} | ${formatGrade(student.grade)}`;
    const studentInfoData = createTextImage(studentInfoText, 16, '500', '#3A434E');
    const studentInfoImage = await pdfDoc.embedPng(studentInfoData);
    const studentInfoDims = studentInfoImage.scale(0.8);
    page.drawImage(studentInfoImage, {
      x: width / 2 - studentInfoDims.width / 2,
      y: height / 2 - qrDims.height / 2 + 20,
      width: studentInfoDims.width,
      height: studentInfoDims.height
    });

    // 하단 안내 박스
    page.drawRectangle({
      x: 40,
      y: 80,
      width: width - 80,
      height: 100,
      color: rgb(1, 0.98, 0.95) // 연한 노란색
    });

    // 안내 문구 1
    const guide1Data = createTextImage('과제 제출 시 이 페이지를 맨 앞에 첨부하세요', 16, '500', BRAND_COLOR);
    const guide1Image = await pdfDoc.embedPng(guide1Data);
    const guide1Dims = guide1Image.scale(0.8);
    page.drawImage(guide1Image, {
      x: width / 2 - guide1Dims.width / 2,
      y: 140,
      width: guide1Dims.width,
      height: guide1Dims.height
    });

    // 안내 문구 2
    const guide2Data = createTextImage('QR 코드가 손상되지 않도록 주의해주세요', 14, 'normal', ACCENT_COLOR);
    const guide2Image = await pdfDoc.embedPng(guide2Data);
    const guide2Dims = guide2Image.scale(0.8);
    page.drawImage(guide2Image, {
      x: width / 2 - guide2Dims.width / 2,
      y: 105,
      width: guide2Dims.width,
      height: guide2Dims.height
    });
  };

  const handleGenerate = async () => {
    if (!selectedLevel) {
      alert('클래스를 선택해주세요');
      return;
    }

    if (selectedStudents.length === 0) {
      alert('최소 한 명 이상의 학생을 선택해주세요');
      return;
    }

    setGenerating(true);

    try {
      const level = levels.find(l => l.id === parseInt(selectedLevel));
      const pdfDoc = await PDFDocument.create();

      // 선택된 학생들의 페이지를 하나의 PDF에 추가
      for (const studentId of selectedStudents) {
        const student = students.find(s => s.id === studentId);
        if (!student) continue;

        await addPageToPDF(pdfDoc, level, student);
      }

      // PDF 저장
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      if (selectedStudents.length === 1) {
        const student = students.find(s => s.id === selectedStudents[0]);
        link.download = `QR_표지_${student.name}_${level.name}.pdf`;
      } else {
        link.download = `QR_표지_${level.name}_${selectedStudents.length}명.pdf`;
      }

      link.click();
      URL.revokeObjectURL(url);

      if (onGenerate) onGenerate();
      handleClose();

    } catch (error) {
      console.error('QR PDF 생성 오류:', error);
      alert('QR PDF 생성 중 오류가 발생했습니다.');
    } finally {
      setGenerating(false);
    }
  };

  const handleClose = () => {
    setSelectedLevel('');
    setSelectedStudents([]);
    setBulkMode(false);
    onClose();
  };

  const handleStudentToggle = (studentId) => {
    if (!bulkMode) {
      // 단일 모드: 한 명만 선택 가능
      setSelectedStudents([studentId]);
    } else {
      // 대량 모드: 여러 명 토글
      setSelectedStudents(prev =>
        prev.includes(studentId)
          ? prev.filter(id => id !== studentId)
          : [...prev, studentId]
      );
    }
  };

  const handleSelectAll = () => {
    if (selectedStudents.length === students.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map(s => s.id));
    }
  };

  // Redux students는 이미 fetchStudents(levelId)로 필터링됨
  const filteredStudents = students;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#FFF',
        borderRadius: '12px',
        padding: 'clamp(16px, 4vw, 30px)',
        width: 'min(600px, 100%)',
        maxWidth: 'min(90vw, calc(100vw - 32px))',
        maxHeight: '80vh',
        overflowY: 'auto', WebkitOverflowScrolling: 'touch',
        boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '25px'
        }}>
          <h2 style={{
            margin: 0,
            color: BRAND_COLOR,
            fontSize: '1.12rem',
            fontWeight: 800,
            letterSpacing: '-0.01em'
          }}>
            QR 표지 생성
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'rgba(14, 20, 27, 0.06)',
              border: 'none',
              width: '28px',
              height: '28px',
              borderRadius: '999px',
              fontSize: '16px',
              cursor: 'pointer',
              color: '#7A8490',
              padding: 0,
              lineHeight: 1,
              flexShrink: 0
            }}
          >
            ×
          </button>
        </div>

        {/* 모드 선택 */}
        <div style={{ marginBottom: '20px', display: 'flex', gap: '5px' }}>
          <button
            onClick={() => {
              setBulkMode(false);
              setSelectedStudents([]);
            }}
            style={{
              flex: 1,
              padding: '9px 14px',
              border: bulkMode ? '1.5px solid #E6EDEC' : `1.5px solid ${BRAND_COLOR}`,
              borderRadius: '999px',
              backgroundColor: bulkMode ? '#FFF' : BRAND_COLOR,
              color: bulkMode ? '#3A434E' : '#FFF',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '0.84rem',
              fontFamily: 'inherit',
              whiteSpace: 'nowrap',
              transition: 'all .12s'
            }}
          >
            단일 생성
          </button>
          <button
            onClick={() => {
              setBulkMode(true);
              setSelectedStudents([]);
            }}
            style={{
              flex: 1,
              padding: '9px 14px',
              border: !bulkMode ? '1.5px solid #E6EDEC' : `1.5px solid ${BRAND_COLOR}`,
              borderRadius: '999px',
              backgroundColor: !bulkMode ? '#FFF' : BRAND_COLOR,
              color: !bulkMode ? '#3A434E' : '#FFF',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '0.84rem',
              fontFamily: 'inherit',
              whiteSpace: 'nowrap',
              transition: 'all .12s'
            }}
          >
            대량 생성
          </button>
        </div>

        {/* 클래스 */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '7px', fontWeight: 800, color: '#7A8490', fontSize: '0.72rem', letterSpacing: '0.06em' }}>
            클래스
          </label>
          <select
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              border: '2px solid #E6EDEC',
              borderRadius: '8px',
              fontSize: '15px',
              boxSizing: 'border-box',
              outline: 'none'
            }}
          >
            <option value="">클래스를 선택하세요</option>
            {levels.map(level => (
              <option key={level.id} value={level.id}>{level.name}</option>
            ))}
          </select>
        </div>

        {/* 학생 선택 */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <label style={{ fontWeight: '600', color: '#3A434E', fontSize: '15px' }}>
              학생 ({selectedStudents.length}명 선택됨)
            </label>
            {bulkMode && filteredStudents.length > 0 && (
              <button
                onClick={handleSelectAll}
                style={{
                  padding: '6px 14px',
                  fontSize: '13px',
                  borderRadius: '6px',
                  backgroundColor: selectedStudents.length === filteredStudents.length ? '#7A8490' : BRAND_COLOR,
                  border: 'none',
                  color: '#FFF',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                {selectedStudents.length === filteredStudents.length ? '전체 해제' : '전체 선택'}
              </button>
            )}
          </div>

          {!selectedLevel ? (
            <div style={{
              padding: 'clamp(16px, 4vw, 30px)',
              border: '2px dashed #E6EDEC',
              borderRadius: '8px',
              textAlign: 'center',
              color: '#B9C1C9',
              backgroundColor: '#f8f8f8'
            }}>
              먼저 클래스를 선택해주세요
            </div>
          ) : filteredStudents.length === 0 ? (
            <div style={{
              padding: 'clamp(16px, 4vw, 30px)',
              border: '2px dashed #E6EDEC',
              borderRadius: '8px',
              textAlign: 'center',
              color: '#B9C1C9'
            }}>
              이 클래스에 등록된 학생이 없습니다
            </div>
          ) : (
            <div style={{
              border: '2px solid #E6EDEC',
              borderRadius: '8px',
              maxHeight: '280px',
              overflowY: 'auto', WebkitOverflowScrolling: 'touch',
              backgroundColor: '#FFF'
            }}>
              {filteredStudents.map(student => (
                <div
                  key={student.id}
                  onClick={() => handleStudentToggle(student.id)}
                  style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #f0f0f0',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    backgroundColor: selectedStudents.includes(student.id) ? '#F2FDFB' : '#FFF',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!selectedStudents.includes(student.id)) {
                      e.currentTarget.style.backgroundColor = '#f8f8f8';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!selectedStudents.includes(student.id)) {
                      e.currentTarget.style.backgroundColor = '#FFF';
                    }
                  }}
                >
                  <input
                    type={bulkMode ? 'checkbox' : 'radio'}
                    checked={selectedStudents.includes(student.id)}
                    onChange={() => {}}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      accentColor: BRAND_COLOR
                    }}
                  />
                  <span style={{
                    flex: 1,
                    fontSize: '15px',
                    color: selectedStudents.includes(student.id) ? BRAND_COLOR : '#3A434E',
                    fontWeight: selectedStudents.includes(student.id) ? '600' : 'normal'
                  }}>
                    {student.name}
                  </span>
                  <span style={{
                    fontSize: '13px',
                    color: '#7A8490',
                    backgroundColor: '#F4F8F7',
                    padding: '4px 10px',
                    borderRadius: '12px'
                  }}>
                    {formatGrade(student.grade)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 안내 */}
        <div style={{
          padding: '11px 14px',
          backgroundColor: '#F4F8F7',
          borderRadius: '12px',
          marginBottom: '20px',
          fontSize: '0.76rem',
          color: '#3A434E',
          lineHeight: '1.6'
        }}>
          <div style={{ fontWeight: 800, marginBottom: '5px', color: '#00A396', fontSize: '0.72rem', letterSpacing: '0.06em' }}>사용 방법</div>
          1. 학생별 QR 표지를 생성하여 출력합니다<br />
          2. 학생이 과제를 제출할 때 이 표지를 맨 앞에 첨부합니다<br />
          3. 스캔 후 일괄 업로드하면 자동으로 학생별로 분류됩니다<br />
          {bulkMode && <><br /><strong>※ 대량 생성 시 하나의 PDF에 여러 페이지로 생성됩니다</strong></>}
        </div>

        {/* 버튼 */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <button
            onClick={handleClose}
            style={{
              padding: '11px 22px',
              fontSize: '0.9rem',
              borderRadius: '999px',
              backgroundColor: '#FFF',
              border: '1.5px solid #E6EDEC',
              color: '#3A434E',
              cursor: 'pointer',
              fontWeight: 700,
              fontFamily: 'inherit'
            }}
          >
            취소
          </button>
          <button
            onClick={handleGenerate}
            disabled={!selectedLevel || selectedStudents.length === 0 || generating}
            style={{
              padding: '11px 24px',
              fontSize: '0.9rem',
              borderRadius: '999px',
              backgroundColor: BRAND_COLOR,
              border: 'none',
              color: '#FFF',
              opacity: generating || !selectedLevel || selectedStudents.length === 0 ? 0.35 : 1,
              cursor: generating || !selectedLevel || selectedStudents.length === 0 ? 'not-allowed' : 'pointer',
              fontWeight: 800,
              fontFamily: 'inherit'
            }}
          >
            {generating ? '생성 중...' : `PDF 생성${selectedStudents.length > 1 ? ` (${selectedStudents.length}명)` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QRCoverGenerator;
